// useMermaid — lazy-loaded mermaid renderer.
//
// Mermaid is ~500KB+, so we dynamic-import it only the first time a chat
// message contains a ```mermaid block. Subsequent calls reuse the same instance.
//
// Render flow per call:
//   1. Caller supplies a unique id + the mermaid source.
//   2. We try `mermaid.parse()` first — partial/streaming syntax throws here.
//   3. On success, `mermaid.render()` returns SVG; on failure we return null
//      so the caller can fall back to a normal code block.

let _mermaidPromise = null
let _idSeq = 0

function isDarkTheme() {
  if (typeof document === 'undefined') return true
  // The dashboard runs dark by default. Heuristic: read the body's
  // background colour and treat low-luminance as dark.
  try {
    const bg = getComputedStyle(document.body).backgroundColor || ''
    const m = bg.match(/rgb[a]?\(([^)]+)\)/)
    if (!m) return true
    const [r, g, b] = m[1].split(',').map(s => parseInt(s.trim(), 10) || 0)
    const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b
    return lum < 128
  } catch { return true }
}

async function loadMermaid() {
  if (_mermaidPromise) return _mermaidPromise
  _mermaidPromise = import('mermaid').then(mod => {
    const mermaid = mod.default || mod
    mermaid.initialize({
      startOnLoad: false,
      theme: isDarkTheme() ? 'dark' : 'default',
      securityLevel: 'strict',
      fontFamily: 'inherit',
      themeVariables: isDarkTheme() ? {
        background: '#0d1220',
        primaryColor: '#3b82f6',
        primaryTextColor: '#e2e8f0',
        primaryBorderColor: '#3b82f6',
        lineColor: '#94a3b8',
        secondaryColor: '#1e2a3e',
        tertiaryColor: '#111827',
      } : {},
    })
    return mermaid
  }).catch(err => {
    _mermaidPromise = null
    throw err
  })
  return _mermaidPromise
}

export function nextMermaidId() {
  _idSeq += 1
  return `mmd-${Date.now().toString(36)}-${_idSeq}`
}

export async function renderMermaid(id, source) {
  if (!source || !source.trim()) return null
  const mermaid = await loadMermaid()
  // parse() throws on malformed/partial syntax — protects us during streaming
  try {
    await mermaid.parse(source)
  } catch {
    return null
  }
  try {
    const { svg } = await mermaid.render(id, source)
    return svg
  } catch {
    return null
  }
}
